import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { lazyWithReload } from "./utils/lazyWithReload.js";
import ErrorBoundary from "./components/UI/ErrorBoundary/ErrorBoundary.jsx";
import Sidebar from "./components/Sidebar/Sidebar.jsx";
import HarmoniqIcon from "./components/Icons/harmoniqIcon.jsx";
import PrivateRoute from "./components/guards/PrivateRoute.jsx";
import RoleRoute from "./components/guards/RoleRoute.jsx";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "./store/slices/authSlice";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  MDO_PLAN_PATH,
  MDO_SETUP_PATH,
  ROUTES,
  TEMPLATE_TAB_PATHS,
  TRIGGER_TAB_PATHS,
  PROJECT_TAB_PATHS,
} from "./constants/routes.js";
import SignIn from "./components/Auth/SignIn.jsx";
import RocheClaimsOS from "./components/RocheClaimsOS.jsx";
// Signup hidden from users — uncomment this import AND the matching
// <Route> below (around the SIGN_IN line) to restore the page.
// import SignUp from './components/Auth/SignUp.jsx'
import VerifyEmail from "./components/Auth/VerifyEmail.jsx";
import SetPassword from "./components/Auth/SetPassword.jsx";
import OAuthSuccess from "./components/OAuth/OAuthSuccess.jsx";
import OAuthPopup from "./components/OAuth/OAuthPopup.jsx";
import useIsMobile from "./hooks/useIsMobile.js";
import useRecentTasksStream from "./hooks/useRecentTasksStream.js";
import { MobileShellContext } from "./context/mobile-shell-context.js";
import MobileTopBar from "./components/MobileTopBar/MobileTopBar.jsx";
import "./AppShell.css";

const Templates = lazyWithReload(
  () => import("./components/Templates/Templates.jsx"),
);
const MyTemplatesTab = lazyWithReload(
  () => import("./components/Templates/tabs/MyTemplatesTab.jsx"),
);
const TeamTemplatesTab = lazyWithReload(
  () => import("./components/Templates/tabs/TeamTemplatesTab.jsx"),
);
const ExploreTemplatesTab = lazyWithReload(
  () => import("./components/Templates/tabs/ExploreTemplatesTab.jsx"),
);
const UserTemplatesTab = lazyWithReload(
  () => import("./components/Templates/tabs/UserTemplatesTab.jsx"),
);
const CompanyLibraryTab = lazyWithReload(
  () => import("./components/Templates/tabs/CompanyLibraryTab.jsx"),
);
const MdoTab = lazyWithReload(
  () => import("./components/Templates/tabs/MdoTab.jsx"),
);
const MdoTemplatePage = lazyWithReload(
  () => import("./components/Templates/mdo/MdoTemplatePage.jsx"),
);
const MdoPlanDetail = lazyWithReload(
  () => import("./components/Templates/mdo/MdoPlanDetail.jsx"),
);
const TemplateDetail = lazyWithReload(
  () => import("./components/Templates/TemplateDetail.jsx"),
);

// Every lazy route runs through lazyWithReload. After a deploy the
// user's cached index.html still references old chunk hashes; navigating
// to a route that lazy-loads a now-404'd chunk would otherwise reject
// with "Failed to fetch dynamically imported module" and blank the
// page. lazyWithReload catches that exact failure, sets a one-shot
// sessionStorage guard, and force-reloads so the user picks up the new
// index.html automatically. The ErrorBoundary wrapping <Suspense> below
// is the visual fallback if a second failure slips past the guard.
const ActiveTab = lazyWithReload(
  () => import("./components/Triggers/tabs/ActiveTab.jsx"),
);
const ScheduledTab = lazyWithReload(
  () => import("./components/Triggers/tabs/ScheduledTab.jsx"),
);
const AllTab = lazyWithReload(
  () => import("./components/Triggers/tabs/AllTab.jsx"),
);
const PausedTab = lazyWithReload(
  () => import("./components/Triggers/tabs/PausedTab.jsx"),
);
const EndedTab = lazyWithReload(
  () => import("./components/Triggers/tabs/EndedTab.jsx"),
);
const ConflictsTab = lazyWithReload(
  () => import("./components/Triggers/tabs/ConflictsTab.jsx"),
);
const AuditLogTab = lazyWithReload(
  () => import("./components/Triggers/tabs/AuditLogTab.jsx"),
);
const TriggerDetail = lazyWithReload(
  () => import("./components/Triggers/TriggerDetail.jsx"),
);

const Projects = lazyWithReload(
  () => import("./components/Projects/Projects.jsx"),
);
const ProjectsActiveTab = lazyWithReload(
  () => import("./components/Projects/tabs/ActiveTab.jsx"),
);
const ProjectsCompletedTab = lazyWithReload(
  () => import("./components/Projects/tabs/CompletedTab.jsx"),
);
const ProjectDetail = lazyWithReload(
  () => import("./components/Projects/ProjectDetail.jsx"),
);

const Workspace = lazyWithReload(
  () => import("./components/Workspace/Workspace.jsx"),
);
const Home = lazyWithReload(() => import("./components/Home/Home.jsx"));
const Relics = lazyWithReload(() => import("./components/Relics/Relics.jsx"));
const Memory = lazyWithReload(() => import("./components/Memory/Memory.jsx"));
const KnowledgeConfig = lazyWithReload(
  () => import("./components/KnowledgeConfig/KnowledgeConfig.jsx"),
);
const Settings = lazyWithReload(
  () => import("./components/Settings/Settings.jsx"),
);
const Notifications = lazyWithReload(
  () => import("./components/Settings/Notifications.jsx"),
);
const Triggers = lazyWithReload(
  () => import("./components/Triggers/Triggers.jsx"),
);
const NotFound = lazyWithReload(
  () => import("./components/NotFound/NotFound.jsx"),
);

function RouteLoader() {
  return (
    <div className="app-route-loader" aria-live="polite" aria-busy="true">
      <HarmoniqIcon size={56} className="app-route-loader-icon" />
    </div>
  );
}

function AppLayout() {
  const isMobile = useIsMobile();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Keep the Sidebar's "My Tasks" list fresh via the recent-tasks event
  // stream instead of interval polling. AppLayout is the authenticated
  // app shell, so this opens on login and tears down on logout.
  useRecentTasksStream();

  // Auto-close the drawer on every route change so a nav-item tap
  // dismisses the overlay without each consumer needing to call
  // closeDrawer() themselves.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Force-close the drawer when the viewport leaves mobile so a
  // user resizing from phone → desktop doesn't leave a stuck-open
  // drawer state lingering.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setDrawerOpen((v) => !v), []);

  const shellValue = useMemo(
    () => ({ isDrawerOpen, openDrawer, closeDrawer, toggleDrawer }),
    [isDrawerOpen, openDrawer, closeDrawer, toggleDrawer],
  );

  return (
    <MobileShellContext.Provider value={shellValue}>
      <div className="app-layout">
        {isMobile ? <MobileTopBar /> : null}
        <Sidebar />
        <ErrorBoundary>
          <Suspense fallback={<RouteLoader />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </div>
    </MobileShellContext.Provider>
  );
}

// Top-level 404: signed-in users get sidebar + page, signed-out users get
// a standalone full-screen 404 (instead of the sign-in redirect that
// PrivateRoute would otherwise issue).
function NotFoundRoute() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  if (isAuthenticated) {
    return (
      <div className="app-layout">
        <Sidebar />
        <ErrorBoundary>
          <Suspense fallback={<RouteLoader />}>
            <NotFound />
          </Suspense>
        </ErrorBoundary>
      </div>
    );
  }
  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteLoader />}>
        <NotFound standalone />
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <Routes>
      <Route path={ROUTES.SIGN_IN} element={<SignIn />} />
      {/* Signup hidden from users — uncomment this line AND the SignUp
          import at the top of this file to restore the page. */}
      {/* <Route path={ROUTES.SIGN_UP} element={<SignUp />} /> */}
      {/* Public — the JWT in ?token=... is the credential, no
          session needed. Lands the user back on /settings/
          notifications with ?verify=success or ?verify=invalid. */}
      <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />
      {/* Public — the token in the slug is the credential, no
          session needed. The page validates the token on mount and
          renders either the create-password form or the link-expired
          card. The bare path (no token slug) also routes here so a
          truncated / malformed link still lands on the expired card
          instead of the 404 page. */}
      <Route path={ROUTES.SET_PASSWORD} element={<SetPassword />} />
      <Route path="/auth/set-password" element={<SetPassword />} />
      <Route path="/oauth/success" element={<OAuthSuccess />} />
      <Route path="/oauth/popup" element={<OAuthPopup />} />
      <Route element={<PrivateRoute />}>
        <Route path="/roche-claimsos" element={<RocheClaimsOS />} />
        <Route element={<AppLayout />}>
          <Route path={ROUTES.HOME} element={<Home />} />
          <Route path={ROUTES.TASK} element={<Workspace page="task" />} />
          <Route
            path={`${ROUTES.TASK}/:taskId`}
            element={<Workspace page="task" />}
          />
          <Route path={ROUTES.TEMPLATES} element={<Templates />}>
            <Route
              index
              element={
                <Navigate to={TEMPLATE_TAB_PATHS.MY_TEMPLATES} replace />
              }
            />
            <Route
              path={TEMPLATE_TAB_PATHS.MY_TEMPLATES}
              element={<MyTemplatesTab />}
            />
            <Route
              path={TEMPLATE_TAB_PATHS.TEAM_TEMPLATES}
              element={<TeamTemplatesTab />}
            />
            <Route
              path={TEMPLATE_TAB_PATHS.EXPLORE}
              element={<ExploreTemplatesTab />}
            />
            <Route
              path={TEMPLATE_TAB_PATHS.USER_TEMPLATES}
              element={
                <RoleRoute allowedRoles={["admin"]}>
                  <UserTemplatesTab />
                </RoleRoute>
              }
            />
            <Route
              path={TEMPLATE_TAB_PATHS.COMPANY_LIBRARY}
              element={
                <RoleRoute allowedRoles={["admin"]}>
                  <CompanyLibraryTab />
                </RoleRoute>
              }
            />
            {/* MDO — Markdown Optimisation. Open to every role, unlike
                the two admin tabs above it. */}
            <Route path={TEMPLATE_TAB_PATHS.MDO} element={<MdoTab />} />
            {/* The builder itself — a detail page, so it renders without
                the tab chrome (see isDetailPage in Templates.jsx). */}
            <Route path={MDO_SETUP_PATH} element={<MdoTemplatePage />} />
            {/* Resuming a saved plan opens the same builder, filled in. */}
            <Route
              path={`${MDO_SETUP_PATH}/:draftId`}
              element={<MdoTemplatePage />}
            />
            {/* A saved plan's own page — what a card on the tab opens. */}
            <Route
              path={`${MDO_PLAN_PATH}/:planId`}
              element={<MdoPlanDetail />}
            />
            <Route path=":id" element={<TemplateDetail />} />
          </Route>
          <Route path={ROUTES.PROJECTS} element={<Projects />}>
            <Route
              index
              element={<Navigate to={PROJECT_TAB_PATHS.ACTIVE} replace />}
            />
            <Route
              path={PROJECT_TAB_PATHS.ACTIVE}
              element={<ProjectsActiveTab />}
            />
            <Route
              path={PROJECT_TAB_PATHS.COMPLETED}
              element={<ProjectsCompletedTab />}
            />
            <Route path=":id" element={<ProjectDetail />} />
          </Route>
          <Route path={ROUTES.TRIGGERS} element={<Triggers />}>
            <Route
              index
              element={<Navigate to={TRIGGER_TAB_PATHS.ALL} replace />}
            />
            <Route path={TRIGGER_TAB_PATHS.ACTIVE} element={<ActiveTab />} />
            <Route path={TRIGGER_TAB_PATHS.PAUSED} element={<PausedTab />} />
            <Route path={TRIGGER_TAB_PATHS.ENDED} element={<EndedTab />} />
            <Route
              path={TRIGGER_TAB_PATHS.SCHEDULED}
              element={<ScheduledTab />}
            />
            <Route path={TRIGGER_TAB_PATHS.ALL} element={<AllTab />} />
            <Route
              path={TRIGGER_TAB_PATHS.CONFLICTS}
              element={<ConflictsTab />}
            />
            <Route
              path={TRIGGER_TAB_PATHS.AUDIT_LOG}
              element={<AuditLogTab />}
            />
            <Route path=":id" element={<TriggerDetail />} />
          </Route>
          <Route path={ROUTES.RELICS} element={<Relics />} />
          <Route path={ROUTES.MEMORY} element={<Memory />} />
          <Route path={ROUTES.KNOWLEDGE_CONFIG} element={<KnowledgeConfig />} />
          {/* DB Config — database connections are configured by super
              administrators only; other users attach shared
              connections from the task composer. */}
          <Route
            path={ROUTES.SETTINGS}
            element={
              <RoleRoute allowedRoles={["super_admin"]}>
                <Settings />
              </RoleRoute>
            }
          />
          <Route path={ROUTES.NOTIFICATIONS} element={<Notifications />} />
        </Route>
      </Route>
      {/* Top-level catch-all — handles both signed-in and signed-out users
          so unauthenticated users don't get redirected to sign-in for
          unknown URLs. */}
      <Route path="*" element={<NotFoundRoute />} />
    </Routes>
  );
}

export default App;
